import { useState } from 'react'
import { Plus, Edit2, Trash2, X, Lock, Key, Shield } from 'lucide-react'

export default function AccessControlEncryptionPage() {
  const [policies, setPolicies] = useState([
    {
      id: 1,
      name: 'Role-Based Access Control (RBAC)',
      type: 'access-control',
      encryptionMethod: 'AES-256',
      status: 'active',
      users: 250,
      lastAudit: new Date().toLocaleDateString()
    },
    {
      id: 2,
      name: 'End-to-End Encryption',
      type: 'encryption',
      encryptionMethod: 'AES-256-GCM',
      status: 'active',
      users: 1500,
      lastAudit: new Date().toLocaleDateString()
    }
  ])

  const [showModal, setShowModal] = useState(false)
  const [editingPolicy, setEditingPolicy] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    type: 'encryption',
    encryptionMethod: 'AES-256'
  })

  const handleCreateClick = () => {
    setEditingPolicy(null)
    setFormData({
      name: '',
      type: 'encryption',
      encryptionMethod: 'AES-256'
    })
    setShowModal(true)
  }

  const handleEditPolicy = (policy) => {
    setEditingPolicy(policy)
    setFormData({
      name: policy.name,
      type: policy.type,
      encryptionMethod: policy.encryptionMethod
    })
    setShowModal(true)
  }

  const handleSavePolicy = () => {
    if (!formData.name) {
      alert('Please fill in all required fields')
      return
    }

    if (editingPolicy) {
      setPolicies(policies.map(p =>
        p.id === editingPolicy.id
          ? { ...p, ...formData, lastAudit: new Date().toLocaleDateString() }
          : p
      ))
    } else {
      const newPolicy = {
        id: Date.now(),
        ...formData,
        status: 'active',
        users: 0,
        lastAudit: new Date().toLocaleDateString()
      }
      setPolicies([...policies, newPolicy])
    }

    setShowModal(false)
  }

  const handleDeletePolicy = (id) => {
    if (confirm('Are you sure you want to delete this policy?')) {
      setPolicies(policies.filter(p => p.id !== id))
    }
  }

  const getTypeIcon = (type) => {
    return type === 'access-control' ? (
      <Shield className="w-5 h-5 text-blue-400" />
    ) : (
      <Lock className="w-5 h-5 text-green-400" />
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Key className="w-8 h-8 text-blue-400" />
            <h1 className="text-3xl font-bold text-white">Access Control & Encryption</h1>
          </div>
          <p className="text-slate-400">Manage encryption standards, access controls, and key management</p>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Active Policies</div>
            <div className="text-3xl font-bold text-blue-400">{policies.length}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Protected Users</div>
            <div className="text-3xl font-bold text-cyan-400">{policies.reduce((sum, p) => sum + p.users, 0)}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Access Control Policies</div>
            <div className="text-3xl font-bold text-green-400">{policies.filter(p => p.type === 'access-control').length}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Encryption Policies</div>
            <div className="text-3xl font-bold text-yellow-400">{policies.filter(p => p.type === 'encryption').length}</div>
          </div>
        </div>

        {/* Create Button */}
        <button
          onClick={handleCreateClick}
          className="mb-6 flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white font-semibold rounded-lg transition-all"
        >
          <Plus className="w-5 h-5" />
          Create Policy
        </button>

        {/* Policies List */}
        <div className="space-y-4">
          {policies.map((policy) => (
            <div
              key={policy.id}
              className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    {getTypeIcon(policy.type)}
                    <h3 className="text-lg font-semibold text-white">{policy.name}</h3>
                    <span className={`inline-flex items-center px-3 py-1 rounded text-xs font-bold ${
                      policy.status === 'active' ? 'bg-green-600/20 text-green-400' : 'bg-red-600/20 text-red-400'
                    }`}>
                      {policy.status.toUpperCase()}
                    </span>
                  </div>
                  <p className="text-slate-400 mb-4">
                    {policy.type === 'access-control' ? 'Access Control Policy' : 'Encryption Policy'}
                  </p>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div className="bg-slate-700/30 rounded-lg p-3">
                      <span className="text-slate-500">Encryption Method</span>
                      <div className="text-white font-semibold mt-1">{policy.encryptionMethod}</div>
                    </div>
                    <div className="bg-slate-700/30 rounded-lg p-3">
                      <span className="text-slate-500">Protected Users</span>
                      <div className="text-white font-semibold mt-1">{policy.users}</div>
                    </div>
                    <div className="bg-slate-700/30 rounded-lg p-3">
                      <span className="text-slate-500">Last Audit</span>
                      <div className="text-white font-semibold mt-1">{policy.lastAudit}</div>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEditPolicy(policy)}
                    className="p-2 bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 rounded-lg transition-all"
                  >
                    <Edit2 className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDeletePolicy(policy.id)}
                    className="p-2 bg-red-600/20 text-red-400 hover:bg-red-600/30 rounded-lg transition-all"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-slate-800 rounded-lg border border-blue-500/20 p-8 max-w-2xl w-full mx-4">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">
                  {editingPolicy ? 'Edit Policy' : 'Create Policy'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-1 hover:bg-slate-700 rounded-lg transition-all"
                >
                  <X className="w-6 h-6 text-slate-400" />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">Policy Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
                    placeholder="e.g., Role-Based Access Control"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Policy Type</label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-blue-500 focus:outline-none"
                    >
                      <option value="access-control">Access Control</option>
                      <option value="encryption">Encryption</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Encryption Method</label>
                    <select
                      value={formData.encryptionMethod}
                      onChange={(e) => setFormData({ ...formData, encryptionMethod: e.target.value })}
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-blue-500 focus:outline-none"
                    >
                      <option value="AES-128">AES-128</option>
                      <option value="AES-192">AES-192</option>
                      <option value="AES-256">AES-256</option>
                      <option value="AES-256-GCM">AES-256-GCM</option>
                      <option value="RSA-2048">RSA-2048</option>
                      <option value="RSA-4096">RSA-4096</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-semibold transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSavePolicy}
                  className="px-6 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white rounded-lg font-semibold transition-all"
                >
                  Save Policy
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
